import React, { Component, ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAppInsights, getHomeLocation } from '../../Services/Global';
import logger from '../../Services/LoggingService';

interface RouteErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  route?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: RouteErrorInfo | null;
  errorId: string;
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
  routeName?: string;
  fallback?: ReactNode;
}

// Functional component for route error display
const RouteErrorDisplay: React.FC<{
  error: Error;
  errorInfo: RouteErrorInfo;
  errorId: string;
  routeName?: string;
  onRetry: () => void;
}> = ({ error, errorInfo, errorId, routeName, onRetry }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const styles = {
    container: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(3),
      minHeight: '400px'
    },
    errorPaper: {
      maxWidth: 600,
      width: '100%',
      padding: theme.spacing(3),
      textAlign: 'center' as const
    },
    errorIcon: {
      fontSize: 48,
      color: theme.palette.warning.main,
      marginBottom: theme.spacing(2)
    },
    actionButtons: {
      marginTop: theme.spacing(3),
      gap: theme.spacing(1),
      flexWrap: 'wrap' as const
    },
    errorChip: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2)
    }
  };

  const handleGoHome = (): void => {
    const homeLocation = getHomeLocation();
    navigate(homeLocation || '/dashboard');
  };

  const handleGoBack = (): void => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  return (
    <Box sx={styles.container}>
      <Paper elevation={2} sx={styles.errorPaper}>
        <WarningIcon sx={styles.errorIcon} />
        
        <Typography variant="h5" component="h2" gutterBottom color="warning.main">
          Page Error
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          There was an error loading this page
          {routeName && ` (${routeName})`}. You can try refreshing the page or navigate 
          to a different section of the application.
        </Typography>

        <Chip 
          label={`Error ID: ${errorId}`}
          variant="outlined"
          size="small"
          sx={styles.errorChip}
        />

        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="center" 
          sx={styles.actionButtons}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Retry Page
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            onClick={handleGoBack}
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Go Back
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={handleGoHome}
            startIcon={<HomeIcon />}
            size="small"
          >
            Go Home
          </Button>
        </Stack>

        {process.env.NODE_ENV === 'development' && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            <AlertTitle>Development Error Details</AlertTitle>
            <Typography variant="body2" component="div">
              <strong>Error:</strong> {error.name}: {error.message}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <strong>Route:</strong> {location.pathname}
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    const errorId = `RTE-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorId = this.state.errorId;
    const routeName = this.props.routeName || 'Unknown Route';
    
    logger.error(`Route Error Boundary (${routeName}) caught an error`, error, {
      errorId: errorId,
      routeName: routeName,
      componentStack: errorInfo.componentStack
    }, {
      component: 'RouteErrorBoundary',
      method: 'componentDidCatch'
    });

    // Log to Application Insights
    try {
      const appInsights = getAppInsights();
      if (appInsights) {
        appInsights.trackException({
          exception: error,
          properties: {
            errorBoundary: 'RouteErrorBoundary',
            routeName: routeName,
            errorId: errorId,
            componentStack: errorInfo.componentStack || '',
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href || 'unknown',
            pathname: window.location.pathname || 'unknown',
            userId: 'unknown'
          }
        });
        
        appInsights.trackEvent({
          name: 'ErrorBoundary:RouteError',
          properties: {
            errorId: errorId,
            errorName: error.name,
            errorMessage: error.message,
            boundary: 'Route',
            routeName: routeName,
            pathname: window.location.pathname || 'unknown',
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (loggingError) {
      logger.error('Failed to log route error to Application Insights', loggingError as Error, {
        routeName: routeName,
        errorId: errorId
      }, {
        component: 'RouteErrorBoundary',
        method: 'componentDidCatch'
      });
    }

    this.setState({
      errorInfo: {
        componentStack: errorInfo.componentStack || '',
        errorBoundary: 'RouteErrorBoundary',
        route: routeName
      }
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <RouteErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          routeName={this.props.routeName}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary; 